import { defineStore } from "pinia";
import axios from "axios";
import type { StockInfo } from "../utils/types";
import { getSongbirdApiBase, getSongbirdWsBase } from "../utils/songbird-client";

type ReferencedStocksInfo = {
	[key: string]: StockInfo;
};

const WS_MAX_RECONNECT_ATTEMPTS = 5;
const WS_BASE_RECONNECT_DELAY_MS = 1000;
const REST_POLL_INTERVAL_MS = 10000;

interface StockStoreState {
	stocks: ReferencedStocksInfo;
	didFetchYTD: boolean;
	activeSymbols: string[];
	/** True once the live WebSocket feed is connected and pushing updates. */
	usingWebSocket: boolean;
	ws: WebSocket | null;
	wsReconnectAttempts: number;
	intervals: {
		stocksInterval?: NodeJS.Timeout;
		wsReconnectTimer?: NodeJS.Timeout;
	};
}

export const useStocksStore = defineStore("stocks", {
	state: (): StockStoreState => ({
		stocks: {},
		didFetchYTD: false,
		activeSymbols: [],
		usingWebSocket: false,
		ws: null,
		wsReconnectAttempts: 0,
		intervals: {}
	}),

	getters: {
		getStockBySymbol: (state) => (symbol: string) => state.stocks[symbol],
		hasActiveIntervals: (state) =>
			Object.keys(state.intervals).some(
				(key) => state.intervals[key as keyof typeof state.intervals] !== undefined
			),
		stockCount: (state) => Object.keys(state.stocks).length
	},

	actions: {
		async fetchStocksData(symbols: string[], includeYtd = false) {
			if (import.meta.server || symbols.length === 0) {
				return { data: [] as StockInfo[] };
			}

			try {
				const params: Record<string, string> = {
					symbol: symbols.join(",")
				};

				if (includeYtd) {
					params.include_ytd = "true";
				}

				const res = await axios.get(`${getSongbirdApiBase()}stocks/quote`, {
					params,
					timeout: 15000,
					validateStatus: () => true
				});

				if (res.status !== 200) {
					console.error(
						`[StocksStore] Stock quote request failed with status ${res.status}`
					);
					return { data: [] as StockInfo[] };
				}

				const payload = res.data?.data ?? res.data;
				const data = Array.isArray(payload) ? payload : [];

				return { data };
			} catch (error) {
				console.error("[StocksStore] Error fetching stock quote:", error);
				return { data: [] as StockInfo[] };
			}
		},

		/** Merge raw quote rows (from REST or the WebSocket feed) into `this.stocks`. */
		mergeQuoteRows(rows: any[]) {
			if (!rows || rows.length === 0) return;

			const payload: ReferencedStocksInfo = {};

			const stock_data: StockInfo[] = rows.map((item: any) => {
				const YTD_CHG = this.stocks[item.SYMBOL]?.YTD_CHG ?? item.YTD_CHG ?? 0;
				const YTD_PCT = this.stocks[item.SYMBOL]?.YTD_PCT ?? item.YTD_PCT ?? 0;
				const HISTORIC_DATA = this.stocks[item.SYMBOL]?.HISTORIC_DATA || null;
				const prices = HISTORIC_DATA?.map((data: any) => data.close) || [];
				const labels = HISTORIC_DATA?.map((data: any) => data.date) || [];

				return {
					...item,
					symbol: item.SYMBOL,
					HISTORIC_DATA,
					prices,
					labels,
					YTD_CHG,
					YTD_PCT
				};
			});

			stock_data.forEach((item: StockInfo) => {
				payload[item.symbol] = { ...item };
			});

			this.stocks = { ...this.stocks, ...payload };
		},

		async taskFetchInfo(newSymbols: string[], includeYtd = false) {
			if (!newSymbols || newSymbols.length === 0) return;

			const res = await this.fetchStocksData(newSymbols, includeYtd);
			this.mergeQuoteRows(res.data);
		},

		/**
		 * Connect to Songbird's `/ws/stocks/quote` WebSocket for push-based
		 * quote updates instead of short-polling `stocks/quote` on a timer.
		 * Falls back to REST polling automatically if the socket can't be
		 * established or repeatedly drops (see `startRestPollingFallback`).
		 */
		connectWebSocket(symbols: string[]) {
			if (!import.meta.client || symbols.length === 0) return;

			const wsBase = getSongbirdWsBase();
			if (!wsBase || wsBase === "ws://" || wsBase === "wss://") {
				console.warn("[StocksStore] WS base not configured, using REST polling");
				this.startRestPollingFallback();
				return;
			}

			const wsUrl = `${wsBase}ws/stocks/quote?symbols=${symbols
				.map((s) => encodeURIComponent(s))
				.join(",")}`;

			try {
				const ws = new WebSocket(wsUrl);

				ws.onopen = () => {
					this.wsReconnectAttempts = 0;
				};

				ws.onmessage = (event) => {
					try {
						const msg = JSON.parse(event.data);

						switch (msg.type) {
							case "connected":
								this.usingWebSocket = true;
								// The push feed may be running as a REST-interval
								// fallback server-side (see stock-quotes.ts) — either
								// way we no longer need our own client-side interval.
								this.stopRestInterval();
								break;

							case "quote_update": {
								const rows = Array.isArray(msg.data)
									? msg.data
									: msg.data
										? [msg.data]
										: [];
								this.mergeQuoteRows(rows);
								break;
							}

							case "status":
								// Outside active trading hours — nothing to merge.
								break;

							case "error":
								console.error("[StocksStore] WS server error:", msg.error);
								break;
						}
					} catch (err) {
						console.error("[StocksStore] Error parsing WS message:", err);
					}
				};

				ws.onclose = () => {
					this.usingWebSocket = false;
					if (this.ws === ws) this.ws = null;
					this.scheduleWsReconnect(symbols);
				};

				ws.onerror = () => {
					// onclose fires right after onerror; reconnect handled there.
				};

				this.ws = ws;
			} catch (error) {
				console.error("[StocksStore] Failed to open WS:", error);
				this.scheduleWsReconnect(symbols);
			}
		},

		scheduleWsReconnect(symbols: string[]) {
			if (this.activeSymbols.length === 0) return; // tracking was stopped

			if (this.wsReconnectAttempts >= WS_MAX_RECONNECT_ATTEMPTS) {
				console.warn(
					"[StocksStore] WS max reconnect attempts reached, falling back to REST polling"
				);
				this.startRestPollingFallback();
				return;
			}

			const delay = Math.min(
				WS_BASE_RECONNECT_DELAY_MS * 2 ** this.wsReconnectAttempts,
				30000
			);

			this.intervals.wsReconnectTimer = setTimeout(() => {
				this.wsReconnectAttempts++;
				this.connectWebSocket(symbols);
			}, delay);
		},

		/** REST short-polling fallback — only used when the WS feed is unavailable. */
		startRestPollingFallback() {
			if (this.intervals.stocksInterval) return;

			this.intervals.stocksInterval = setInterval(async () => {
				try {
					await this.taskFetchInfo(this.activeSymbols, false);
				} catch (error) {
					console.error("[StocksStore] Interval error:", error);
					this.stopRestInterval();
				}
			}, REST_POLL_INTERVAL_MS);
		},

		stopRestInterval() {
			if (this.intervals.stocksInterval) {
				clearInterval(this.intervals.stocksInterval);
				this.intervals.stocksInterval = undefined;
			}
		},

		stopWebSocket() {
			if (this.intervals.wsReconnectTimer) {
				clearTimeout(this.intervals.wsReconnectTimer);
				this.intervals.wsReconnectTimer = undefined;
			}
			this.ws?.close();
			this.ws = null;
			this.usingWebSocket = false;
			this.wsReconnectAttempts = 0;
		},

		async startTrackingSymbols(symbols: string[]) {
			this.stopTrackingSymbols();

			this.activeSymbols = [...symbols];

			if (symbols.length === 0) return;

			// Fetch once immediately via REST for an instant first paint,
			// regardless of which transport ends up serving live updates.
			await this.taskFetchInfo(symbols, true);

			this.connectWebSocket(symbols);
		},

		stopTrackingSymbols() {
			this.stopWebSocket();
			this.stopRestInterval();

			this.activeSymbols = [];
			this.stocks = {};
			this.didFetchYTD = false;
		},

		cleanup() {
			this.stopTrackingSymbols();
		},

		reset() {
			this.cleanup();
			this.stocks = {};
			this.didFetchYTD = false;
			this.activeSymbols = [];
		}
	}
});
